// src/components/PrintDesigner/InvoiceExportDialog.jsx
// نافذة تنزيل / مشاركة الفاتورة PDF مع اختيار القالب — يبدأ دائمًا بالقالب الافتراضي ★
//
//   <InvoiceExportDialog invGlobalID={id} action="pdf" | "share" onClose={() => setExport(null)} />
import React, { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Box, Typography, Button, CircularProgress, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import StarIcon from "@mui/icons-material/Star";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { toast } from "react-toastify";
import PrintLayoutRenderer from "./PrintLayoutRenderer";
import { loadInvoiceForPrint, useInvoiceTemplates, invoicePdf, sharePdfBlob } from "./printData";
import { PAPER, paperLabel } from "./printTemplates";

const ACCENT = "#4F46E5";
const PX_PER_MM = 96 / 25.4;
const PREVIEW_W = 360; // عرض المعاينة بالبكسل

export default function InvoiceExportDialog({ invGlobalID, action = "pdf", onClose }) {
  const [state, setState] = useState({ loading: true, data: null, layout: null });
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!invGlobalID) return;
    let alive = true;
    setState({ loading: true, data: null, layout: null });
    loadInvoiceForPrint(invGlobalID)
      .then(r => { if (alive) setState({ loading: false, ...r }); })
      .catch(() => { if (alive) { toast.error("تعذر تحميل بيانات الفاتورة"); closeRef.current?.(); } });
    return () => { alive = false; };
  }, [invGlobalID]);

  return (
    <Dialog open={!!invGlobalID} onClose={onClose} dir="rtl" maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}>
      {state.loading ? (
        <Box sx={{ display: "grid", placeItems: "center", gap: 1, py: 8 }}>
          <CircularProgress sx={{ color: ACCENT }} />
          <Typography sx={{ color: "#64748b" }}>جاري تجهيز الفاتورة...</Typography>
        </Box>
      ) : (
        <ExportBody data={state.data} layout={state.layout} action={action} onClose={onClose} />
      )}
    </Dialog>
  );
}

function ExportBody({ data, layout, action, onClose }) {
  const { options, current, pick } = useInvoiceTemplates(layout, data?.invoiceTypeId ?? data?.invoiceType);
  const pdfRef = useRef(null);
  const [busy, setBusy] = useState(null);

  const paper = PAPER[current?.layout?.page?.size] || PAPER.A4;
  const scale = Math.min(1, PREVIEW_W / (paper.w * PX_PER_MM));

  const run = async (kind) => {
    if (!pdfRef.current || !current) return;
    setBusy(kind);
    try {
      const worker = invoicePdf(pdfRef.current, current, data);
      if (kind === "pdf") {
        await worker.save();
        onClose?.();
      } else {
        const blob = await worker.output("blob");
        const shared = await sharePdfBlob(blob, data.invoiceNumber);
        if (!shared) toast.info("تم تحميل الملف لأن المشاركة غير مدعومة في هذا المتصفح");
        onClose?.();
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(kind === "pdf" ? "فشل إنشاء ملف PDF" : "فشل مشاركة الفاتورة");
    } finally {
      setBusy(null);
    }
  };

  const primary = action === "share" ? "share" : "pdf";

  return (
    <>
      {/* الرأس */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.75, color: "#fff",
        background: "linear-gradient(135deg, #4F46E5 0%, #6366F1 60%, #818CF8 100%)" }}>
        {primary === "share" ? <ShareOutlinedIcon /> : <PictureAsPdfOutlinedIcon />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>{primary === "share" ? "مشاركة الفاتورة" : "تنزيل الفاتورة PDF"}</Typography>
          <Typography sx={{ fontSize: 13, opacity: .85 }} noWrap>
            فاتورة رقم {data?.invoiceNumber} {data?.customer?.name ? `— ${data.customer.name}` : ""}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 400px" }, minHeight: 420 }}>
        {/* القوالب */}
        <Box sx={{ p: 2, borderInlineEnd: { md: "1px solid #eef0f4" } }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#64748b", mb: 1 }}>اختر القالب</Typography>
          <Box sx={{ display: "grid", gap: 1 }}>
            {options.map(o => {
              const on = o.id === current?.id;
              return (
                <Box key={o.id} role="button" tabIndex={0} onClick={() => pick(o.id)}
                  onKeyDown={e => { if (e.key === "Enter") pick(o.id); }}
                  sx={{
                    display: "flex", alignItems: "center", gap: 1.25, p: 1.25, borderRadius: "12px", cursor: "pointer",
                    border: `1.5px solid ${on ? ACCENT : "#e5e7eb"}`, bgcolor: on ? "#eef2ff" : "#fff",
                    transition: ".15s", "&:hover": { borderColor: ACCENT },
                  }}>
                  {on ? <CheckCircleIcon sx={{ color: ACCENT }} /> : <Box sx={{ width: 24, height: 24, borderRadius: "50%", border: "2px solid #cbd5e1", flexShrink: 0 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: on ? ACCENT : "#1e293b" }} noWrap>{o.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{paperLabel(o.layout)}</Typography>
                  </Box>
                  {o.isDefault && (
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: .4, px: 1, py: .25, borderRadius: "999px", bgcolor: "#fef3c7", color: "#92400e", fontSize: 11.5, fontWeight: 800 }}>
                      <StarIcon sx={{ fontSize: 14, color: "#f59e0b" }} /> افتراضي
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* المعاينة */}
        <Box sx={{ bgcolor: "#eef0f4", p: 2, display: "flex", justifyContent: "center", overflow: "auto", maxHeight: { md: "62vh" } }}>
          {current && (
            <Box sx={{ width: paper.w * PX_PER_MM * scale, height: "fit-content", flexShrink: 0, boxShadow: "0 8px 24px -12px rgba(15,23,42,.4)", bgcolor: "#fff", overflow: "hidden" }}>
              <Box sx={{ zoom: scale }}>
                <PrintLayoutRenderer key={current.id} layout={current.layout} data={data} />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1, borderTop: "1px solid #eef0f4" }}>
        <Button variant="text" onClick={onClose} sx={{ borderRadius: "10px", fontWeight: 700, color: "#64748b", bgcolor: "transparent", "&:hover": { bgcolor: "#f1f5f9" } }}>إلغاء</Button>
        <Box sx={{ flex: 1 }} />
        {(primary === "pdf" ? ["share", "pdf"] : ["pdf", "share"]).map(kind => (
          <Button key={kind} variant={kind === primary ? "contained" : "outlined"} disabled={!!busy}
            onClick={() => run(kind)}
            startIcon={busy === kind ? <CircularProgress size={16} color="inherit" /> : kind === "pdf" ? <PictureAsPdfOutlinedIcon /> : <ShareOutlinedIcon />}
            sx={{
              borderRadius: "10px", fontWeight: 700, px: 2.5, "& .MuiButton-startIcon": { ml: .75, mr: -.25 },
              ...(kind === primary ? { bgcolor: ACCENT, "&:hover": { bgcolor: "#4338CA" } } : { color: ACCENT, borderColor: "#c7d2fe" }),
            }}>
            {kind === "pdf" ? "تنزيل PDF" : "مشاركة"}
          </Button>
        ))}
      </DialogActions>

      {/* نسخة بالحجم الحقيقي خارج الشاشة — منها يُنشأ الـ PDF */}
      <Box aria-hidden sx={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }}>
        {current && <PrintLayoutRenderer key={`pdf-${current.id}`} ref={pdfRef} layout={current.layout} data={data} />}
      </Box>
    </>
  );
}

